import {
  AssignmentBlockOutputResponseModel,
  AssignmentBlockOutputResultModel,
  AssignmentModel,
  AssignmentTaskSlideType,
  UserModel
} from '@models';
import { UploadedFileModel } from '@models/assignments';
import * as Sentry from '@sentry/nextjs';
import { useStore } from '@store';
import React, { createContext, useContext, useEffect, useReducer, useRef } from 'react';

const initializeBlockUserInputs = (
  blockOutputResults?: AssignmentBlockOutputResultModel[]
): Record<string, UploadedFileModel[]> => {
  return (
    blockOutputResults?.reduce(
      (acc, result) => {
        // Initialize file upload inputs from server results
        if (result.uploadedFiles && result.uploadedFiles.length > 0) {
          acc[result.blockEntityId] = result.uploadedFiles.map(
            (file) =>
              ({
                file_upload_id: file.file_upload_id,
                mediaitem_id: file.mediaitem_id,
                original_filename: file.original_filename,
                content_type: file.content_type
              }) as UploadedFileModel
          );
        }
        return acc;
      },
      {} as Record<string, UploadedFileModel[]>
    ) || {}
  );
};

export enum AssignmentTaskActionType {
  SET_ASSIGNMENT = 'SET_ASSIGNMENT',
  SHOW_NEXT_SLIDE = 'SHOW_NEXT_SLIDE',
  SHOW_PREV_SLIDE = 'SHOW_PREV_SLIDE',
  SHOW_SLIDE = 'SHOW_SLIDE',
  SHOW_TOC = 'SHOW_TOC',
  HIDE_TOC = 'HIDE_TOC',
  STOP_AUTOPLAY = 'STOP_AUTOPLAY',
  START_AUTOPLAY = 'START_AUTOPLAY',
  CHECK_BLOCK_USER_INPUT = 'CHECK_BLOCK_USER_INPUT',
  SET_BLOCK_OUTPUT_RESPONSE = 'SET_BLOCK_OUTPUT_RESPONSE',
  SET_BLOCKS_WITH_MISSING_USER_INPUT = 'SET_BLOCKS_WITH_MISSING_USER_INPUT',
  SET_BLOCK_USER_INPUT = 'SET_BLOCK_USER_INPUT',
  SET_HIDE_TOP_NAVIGATION = 'SET_HIDE_TOP_NAVIGATION',
  SET_GAME_PROGRESS = 'SET_GAME_PROGRESS',
  SHAKE_BLOCK_INPUT = 'SHAKE_BLOCK_INPUT',
  SET_HIDE_BOTTOM_NAVIGATION = 'SET_HIDE_BOTTOM_NAVIGATION',
  SET_UPLOAD_CONFIRMED = 'SET_UPLOAD_CONFIRMED',
  SET_CHECK_BUTTON_DISABLED = 'SET_CHECK_BUTTON_DISABLED',
  SET_FILE_UPLOAD_CHECK_BUTTON_LOADING = 'SET_FILE_UPLOAD_CHECK_BUTTON_LOADING',
  RESET_BLOCK = 'RESET_BLOCK',
  END_GAME = 'END_GAME',
  REQUEST_GAME_END = 'REQUEST_GAME_END',
  GAME_STARTED = 'GAME_STARTED',
  REQUEST_GAME_START = 'REQUEST_GAME_START',
  REQUEST_GAME_RETRY = 'REQUEST_GAME_RETRY',
  UPDATE_GAME_UI_STATE = 'UPDATE_GAME_UI_STATE',
  SET_BLOCK_OUTPUT_ERROR = 'SET_BLOCK_OUTPUT_ERROR',
  SET_BLOCK_OUTPUT_STALLED = 'SET_BLOCK_OUTPUT_STALLED',
  CLEAR_BLOCK_OUTPUT_LOADING = 'CLEAR_BLOCK_OUTPUT_LOADING'
}

export interface GameStateModel {
  started: boolean;
  startRequested: boolean;
  retryRequested: boolean;
  endRequested: boolean;
  score: { correct: number; total: number };
  totalProblems: number;
  navigation: {
    action: boolean;
    next: boolean;
    prev: boolean;
  };
  // Slide → nav UI signals
  showingFeedback: boolean;
  isEvaluating: boolean;
  canCheck: boolean;
  canGoPrev: boolean;
  shouldPulseNext: boolean;
  resetRequests: boolean;
}

export interface AssignmentTaskStateModel {
  tocIsShown: boolean;
  currentSlideType: AssignmentTaskSlideType;
  currentBlockEntityId: string | null;
  assignment: AssignmentModel;
  mentor: UserModel;
  blockOutputResponses: Record<string, AssignmentBlockOutputResponseModel>;
  blocksWithMissingUserInput: string[];
  blockUserInputs: Record<string, any>;
  hideTopNavigation: boolean;
  shakeBlockEntityId: string | null;
  hideBottomNavigation: boolean;
  uploadConfirmed: boolean;
  fileUploadCheckButtonDisabled: boolean;
  fileUploadCheckButtonLoading: boolean;
  game: GameStateModel;
}

export interface AssignmentTaskActionModel {
  type: AssignmentTaskActionType;
  payload?: any;
}

export function assignmentTaskReducer(
  state: AssignmentTaskStateModel,
  action: AssignmentTaskActionModel
): AssignmentTaskStateModel {
  let currentBlockIdx: number | null = null;

  const numberOfBlocks = state.assignment.node?.task?.blocks?.length || 0;

  switch (action.type) {
    case AssignmentTaskActionType.SET_ASSIGNMENT:
      if (state.assignment?.nodeEntityId !== action.payload.assignment.nodeEntityId) {
        const preserveState = action.payload.preserveState === true;

        if (preserveState) {
          return {
            ...state,
            assignment: action.payload.assignment,
            blockOutputResponses:
              action.payload.assignment.blockOutputResults?.reduce(
                (acc, result) => {
                  acc[result.blockEntityId] = {
                    isLoading: false,
                    isError: false,
                    blockOutputResult: result
                  };
                  return acc;
                },
                {} as Record<string, AssignmentBlockOutputResponseModel>
              ) || {},
            blockUserInputs: initializeBlockUserInputs(action.payload.assignment.blockOutputResults)
          };
        } else {
          // Only reset state if it's a completely different assignment
          return {
            ...state,
            currentSlideType: AssignmentTaskSlideType.START_SLIDE,
            currentBlockEntityId: null,
            tocIsShown: false,
            assignment: action.payload.assignment,
            blockOutputResponses:
              action.payload.assignment.blockOutputResults?.reduce(
                (acc, result) => {
                  acc[result.blockEntityId] = {
                    isLoading: false,
                    isError: false,
                    blockOutputResult: result
                  };
                  return acc;
                },
                {} as Record<string, AssignmentBlockOutputResponseModel>
              ) || {},
            blocksWithMissingUserInput: [],
            blockUserInputs: initializeBlockUserInputs(action.payload.assignment.blockOutputResults)
          };
        }
      }
      if (action.payload.refreshBlockOutputs && action.payload.assignment.blockOutputResults) {
        return {
          ...state,
          assignment: action.payload.assignment,
          blockOutputResponses: action.payload.assignment.blockOutputResults.reduce(
            (acc, result) => {
              acc[result.blockEntityId] = { isLoading: false, blockOutputResult: result };
              return acc;
            },
            {} as Record<string, AssignmentBlockOutputResponseModel>
          )
        };
      }
      // Same assignment ID - just update the assignment data, preserve UI state
      return {
        ...state,
        assignment: action.payload.assignment
      };

    case AssignmentTaskActionType.SHOW_NEXT_SLIDE:
      // Already at end, no-op
      if (state.currentSlideType === AssignmentTaskSlideType.END_SLIDE) {
        return state;
      }
      // START -> BLOCK
      if (state.currentSlideType === AssignmentTaskSlideType.START_SLIDE) {
        // if there are no blocks, show the end slide
        if (numberOfBlocks === 0) {
          return {
            ...state,
            currentSlideType: AssignmentTaskSlideType.END_SLIDE,
            currentBlockEntityId: null
          };
        }
        return {
          ...state,
          currentSlideType: AssignmentTaskSlideType.BLOCK,
          currentBlockEntityId: state.assignment.node.task.blocks[0].entityId
        };
      }
      // LAST BLOCK -> END
      if (
        state.currentBlockEntityId ===
        state.assignment.node.task.blocks[numberOfBlocks - 1].entityId
      ) {
        return {
          ...state,
          currentSlideType: AssignmentTaskSlideType.END_SLIDE,
          currentBlockEntityId: null
        };
      }

      currentBlockIdx = state.assignment.node.task.blocks.findIndex(
        (block) => block.entityId === state.currentBlockEntityId
      );
      // NEXT BLOCK
      return {
        ...state,
        currentSlideType: AssignmentTaskSlideType.BLOCK,
        currentBlockEntityId: state.assignment.node.task.blocks[currentBlockIdx + 1].entityId
      };

    case AssignmentTaskActionType.SHOW_PREV_SLIDE:
      // No prior slide on START; bail before the block index underflows.
      if (state.currentSlideType === AssignmentTaskSlideType.START_SLIDE) {
        return state;
      }
      // END -> LAST BLOCK
      if (state.currentSlideType === AssignmentTaskSlideType.END_SLIDE) {
        // if there are no blocks, show the start slide
        if (numberOfBlocks === 0) {
          return {
            ...state,
            currentSlideType: AssignmentTaskSlideType.START_SLIDE,
            currentBlockEntityId: null
          };
        }
        return {
          ...state,
          currentSlideType: AssignmentTaskSlideType.BLOCK,
          currentBlockEntityId: state.assignment.node.task.blocks[numberOfBlocks - 1].entityId
        };
      }
      // FIRST BLOCK -> START
      if (state.currentBlockEntityId === state.assignment.node.task.blocks[0].entityId) {
        return {
          ...state,
          currentSlideType: AssignmentTaskSlideType.START_SLIDE,
          currentBlockEntityId: null
        };
      }

      currentBlockIdx = state.assignment.node.task.blocks.findIndex(
        (block) => block.entityId === state.currentBlockEntityId
      );
      // PREV BLOCK
      return {
        ...state,
        currentSlideType: AssignmentTaskSlideType.BLOCK,
        currentBlockEntityId: state.assignment.node.task.blocks[currentBlockIdx - 1].entityId
      };

    case AssignmentTaskActionType.SHOW_SLIDE:
      return {
        ...state,
        currentSlideType: action.payload.slideType,
        currentBlockEntityId: action.payload.blockEntityId,
        tocIsShown: false
      };

    case AssignmentTaskActionType.SHOW_TOC:
      return { ...state, tocIsShown: true };

    case AssignmentTaskActionType.HIDE_TOC:
      return { ...state, tocIsShown: false };

    case AssignmentTaskActionType.CHECK_BLOCK_USER_INPUT:
      return {
        ...state,
        blockOutputResponses: {
          ...state.blockOutputResponses,
          [action.payload.blockEntityId]: {
            isLoading: true,
            isError: false,
            blockOutputResult: null
          }
        },
        blocksWithMissingUserInput: state.blocksWithMissingUserInput.filter(
          (blockEntityId) => blockEntityId !== action.payload.blockEntityId
        )
      };

    case AssignmentTaskActionType.SET_BLOCK_OUTPUT_RESPONSE:
      return {
        ...state,
        blockOutputResponses: {
          ...state.blockOutputResponses,
          [action.payload.blockEntityId]: {
            isLoading: false,
            isError: false,
            blockOutputResult: action.payload.response
          }
        }
      };

    case AssignmentTaskActionType.SET_BLOCK_OUTPUT_ERROR:
      return {
        ...state,
        blockOutputResponses: {
          ...state.blockOutputResponses,
          [action.payload.blockEntityId]: {
            isLoading: false,
            isError: true,
            blockOutputResult: null
          }
        }
      };

    // The grading may still run on the server, so the error must not offer a resubmit.
    case AssignmentTaskActionType.SET_BLOCK_OUTPUT_STALLED:
      return {
        ...state,
        blockOutputResponses: {
          ...state.blockOutputResponses,
          [action.payload.blockEntityId]: {
            isLoading: false,
            isError: true,
            blockOutputResult: null,
            isGradingInFlight: true
          }
        }
      };

    case AssignmentTaskActionType.CLEAR_BLOCK_OUTPUT_LOADING: {
      const blockOutputResponse = state.blockOutputResponses[action.payload.blockEntityId];
      if (!blockOutputResponse?.isLoading) return state;
      return {
        ...state,
        blockOutputResponses: {
          ...state.blockOutputResponses,
          [action.payload.blockEntityId]: { ...blockOutputResponse, isLoading: false }
        }
      };
    }

    case AssignmentTaskActionType.SET_BLOCKS_WITH_MISSING_USER_INPUT:
      return {
        ...state,
        blocksWithMissingUserInput: action.payload.blocksWithMissingUserInput
      };

    case AssignmentTaskActionType.SET_BLOCK_USER_INPUT:
      return {
        ...state,
        blockUserInputs: {
          ...state.blockUserInputs,
          [action.payload.blockEntityId]: action.payload.value
        }
      };

    case AssignmentTaskActionType.SET_HIDE_TOP_NAVIGATION:
      return {
        ...state,
        hideTopNavigation: action.payload.hideTopNavigation
      };

    case AssignmentTaskActionType.SET_GAME_PROGRESS:
      return {
        ...state,
        game: {
          ...state.game,
          score: action.payload.score ?? state.game.score,
          totalProblems: action.payload.totalProblems ?? state.game.totalProblems,
          started: action.payload.started ?? state.game.started,
          retryRequested: false
        }
      };

    case AssignmentTaskActionType.GAME_STARTED:
      return {
        ...state,
        game: {
          ...state.game,
          started: true,
          startRequested: false
        }
      };

    case AssignmentTaskActionType.REQUEST_GAME_START:
      return {
        ...state,
        game: {
          ...state.game,
          startRequested: true
        }
      };

    case AssignmentTaskActionType.REQUEST_GAME_RETRY:
      return {
        ...state,
        game: {
          ...state.game,
          retryRequested: true,
          started: true
        },
        blockOutputResponses: action.payload?.blockEntityId
          ? {
              ...state.blockOutputResponses,
              [action.payload.blockEntityId]: {
                isLoading: false,
                isError: false,
                blockOutputResult: null
              }
            }
          : state.blockOutputResponses
      };

    case AssignmentTaskActionType.UPDATE_GAME_UI_STATE: {
      const payload = (action.payload ?? {}) as Partial<GameStateModel>;
      const shouldResetNavigation = (payload as { resetRequests?: boolean }).resetRequests === true;
      return {
        ...state,
        game: {
          ...state.game,
          ...payload,
          navigation: shouldResetNavigation
            ? { action: false, next: false, prev: false }
            : (payload.navigation ?? state.game.navigation)
        }
      };
    }

    case AssignmentTaskActionType.SHAKE_BLOCK_INPUT:
      return {
        ...state,
        shakeBlockEntityId: action.payload.blockEntityId
      };

    case AssignmentTaskActionType.SET_HIDE_BOTTOM_NAVIGATION:
      return {
        ...state,
        hideBottomNavigation: action.payload.hideBottomNavigation
      };

    case AssignmentTaskActionType.SET_UPLOAD_CONFIRMED:
      if (action.payload.uploadConfirmed) {
        Sentry.captureMessage('HOMEWORK_UPLOAD_CONFIRMED', {
          extra: {
            userId: state.assignment.userId,
            goalSubscriptionId: state.assignment.goalSubscriptionId,
            blockEntityId: state.currentBlockEntityId,
            uploadConfirmed: action.payload.uploadConfirmed
          }
        });
      }
      return {
        ...state,
        uploadConfirmed: action.payload.uploadConfirmed
      };

    case AssignmentTaskActionType.SET_CHECK_BUTTON_DISABLED:
      return {
        ...state,
        fileUploadCheckButtonDisabled: action.payload.disabled
      };

    case AssignmentTaskActionType.SET_FILE_UPLOAD_CHECK_BUTTON_LOADING:
      return {
        ...state,
        fileUploadCheckButtonLoading: action.payload.loading
      };

    case AssignmentTaskActionType.RESET_BLOCK:
      return {
        ...state,
        uploadConfirmed:
          state.currentBlockEntityId === action.payload.blockEntityId
            ? false
            : state.uploadConfirmed,
        blockUserInputs: {
          ...state.blockUserInputs,
          [action.payload.blockEntityId]: []
        },
        blockOutputResponses: {
          ...state.blockOutputResponses,
          [action.payload.blockEntityId]: {
            isLoading: false,
            isError: false,
            blockOutputResult: null
          }
        }
      };

    case AssignmentTaskActionType.REQUEST_GAME_END:
      return {
        ...state,
        game: {
          ...state.game,
          endRequested: true
        }
      };

    case AssignmentTaskActionType.END_GAME:
      return {
        ...state,
        hideTopNavigation: false,
        hideBottomNavigation: false,
        game: {
          started: false,
          startRequested: false,
          retryRequested: false,
          endRequested: false,
          score: { correct: 0, total: 0 },
          totalProblems: 0,
          navigation: { action: false, next: false, prev: false },
          showingFeedback: false,
          isEvaluating: false,
          canCheck: false,
          canGoPrev: false,
          shouldPulseNext: false,
          resetRequests: false
        }
      };

    default:
      return state;
  }
}

export const defaultGameState: GameStateModel = {
  started: false,
  startRequested: false,
  retryRequested: false,
  endRequested: false,
  score: { correct: 0, total: 0 },
  totalProblems: 0,
  navigation: { action: false, next: false, prev: false },
  showingFeedback: false,
  isEvaluating: false,
  canCheck: false,
  canGoPrev: false,
  shouldPulseNext: false,
  resetRequests: false
};

const defaultState = {
  state: {
    currentSlideType: AssignmentTaskSlideType.START_SLIDE,
    currentBlockEntityId: null,
    tocIsShown: false,
    assignment: null,
    mentor: null,
    blockOutputResponses: {} as Record<string, AssignmentBlockOutputResponseModel>,
    blocksWithMissingUserInput: [],
    blockUserInputs: {},
    hideTopNavigation: false,
    shakeBlockEntityId: null,
    hideBottomNavigation: false,
    uploadConfirmed: false,
    fileUploadCheckButtonDisabled: false,
    fileUploadCheckButtonLoading: false,
    game: defaultGameState
  },
  dispatch: (action: AssignmentTaskActionModel) => {},
  isEditorMode: false,
  isReadOnly: false,
  source: undefined as string | undefined
};

const AssignmentTaskContext = createContext(defaultState);

export const AssignmentTaskProvider = ({
  children,
  assignment,
  mentor,
  isEditorMode = false,
  isReadOnly = false,
  source,
  landOnEndSlide = false
}: {
  children: React.ReactNode;
  assignment: AssignmentModel;
  mentor: UserModel;
  isEditorMode?: boolean;
  isReadOnly?: boolean;
  source?: string;
  landOnEndSlide?: boolean;
}) => {
  const { getAssignmentTaskState, setAssignmentTaskState } = useStore((state) => ({
    getAssignmentTaskState: state.getAssignmentTaskState,
    setAssignmentTaskState: state.setAssignmentTaskState
  }));

  // Get existing state from store or create initial state
  const getInitialState = () => {
    const existingState = getAssignmentTaskState(
      assignment.nodeEntityId,
      assignment.goalSubscriptionId
    );

    if (existingState) {
      // Return existing state with current assignment and mentor
      const existingAny = existingState as any;
      return {
        ...existingState,
        assignment,
        mentor,
        blockUserInputs: existingAny.blockUserInputs || {},
        hideTopNavigation: existingState.hideTopNavigation ?? false,
        shakeBlockEntityId: existingAny.shakeBlockEntityId || null,
        hideBottomNavigation: existingAny.hideBottomNavigation ?? false,
        uploadConfirmed: existingAny.uploadConfirmed ?? false,
        fileUploadCheckButtonDisabled: existingAny.fileUploadCheckButtonDisabled ?? false,
        fileUploadCheckButtonLoading: existingAny.fileUploadCheckButtonLoading ?? false,
        game: {
          ...defaultGameState,
          // Preserve score/totalProblems from persisted state (migration from old flat shape)
          score: existingAny.game?.score ?? existingAny.gameScore ?? defaultGameState.score,
          totalProblems:
            existingAny.game?.totalProblems ??
            existingAny.gameTotalProblems ??
            defaultGameState.totalProblems,
          started: existingAny.game?.started ?? existingAny.gameStarted ?? defaultGameState.started
        }
      };
    }

    // Create initial state only if no existing state found
    return {
      currentSlideType: AssignmentTaskSlideType.START_SLIDE,
      currentBlockEntityId: null,
      tocIsShown: false,
      assignment,
      mentor,
      playableEntity: null,
      blockOutputResponses:
        assignment.blockOutputResults?.reduce(
          (acc, result) => {
            acc[result.blockEntityId] = {
              isLoading: false,
              isError: false,
              blockOutputResult: result
            };
            return acc;
          },
          {} as Record<
            string,
            {
              isLoading: boolean;
              isError: boolean;
              blockOutputResult: AssignmentBlockOutputResultModel;
            }
          >
        ) || {},
      blocksWithMissingUserInput: [],
      hideTopNavigation: false,
      blockUserInputs: initializeBlockUserInputs(assignment.blockOutputResults),
      shakeBlockEntityId: null,
      hideBottomNavigation: false,
      uploadConfirmed: false,
      fileUploadCheckButtonDisabled: false,
      fileUploadCheckButtonLoading: false,
      game: defaultGameState
    };
  };

  // Use the pure reducer without side effects
  const [state, dispatch] = useReducer(assignmentTaskReducer, getInitialState());

  // Persist state changes to store after render (to avoid setState during render warning)
  useEffect(() => {
    // Persist the state to store (excluding assignment and mentor which are managed separately)
    const { assignment: _, mentor: __, ...stateToStore } = state;
    setAssignmentTaskState(
      assignment.nodeEntityId,
      assignment.goalSubscriptionId,
      stateToStore as AssignmentTaskStateModel
    );
  }, [state, assignment.nodeEntityId, assignment.goalSubscriptionId, setAssignmentTaskState]);

  // Track previous assignment ID to detect changes across remounts
  const prevAssignmentIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const assignmentIdChanged = prevAssignmentIdRef.current !== assignment.node?.entityId;

    // Update ref for next render
    prevAssignmentIdRef.current = assignment.node?.entityId;

    // Check if assignment has changed (ID or task data)
    // FIXME : this seems to be the same as above
    const stateAssignmentIdMismatch = state.assignment?.nodeEntityId !== assignment.nodeEntityId;

    // Handle assignment ID change (new assignment loaded)
    if (stateAssignmentIdMismatch || assignmentIdChanged) {
      dispatch({
        type: AssignmentTaskActionType.SET_ASSIGNMENT,
        payload: {
          assignment,
          preserveState: false
        }
      });
      return;
    }
    const taskEntityIdChanged =
      state.assignment?.node?.task?.entityId !== assignment.node?.task?.entityId;
    const taskUpdatedAtChanged =
      state.assignment?.node?.task?.updatedAt !== assignment.node?.task?.updatedAt;

    // Also check for changes in task blocks and title (main editing areas)
    const taskTitleChanged = state.assignment?.node?.task?.title !== assignment.node?.task?.title;
    const taskBlocksChanged =
      state.assignment?.node?.task?.blocks !== assignment.node?.task?.blocks;

    if (taskEntityIdChanged || taskUpdatedAtChanged || taskTitleChanged || taskBlocksChanged) {
      dispatch({
        type: AssignmentTaskActionType.SET_ASSIGNMENT,
        payload: {
          assignment,
          preserveState: true
        }
      });
      return;
    }
  }, [
    assignment.nodeEntityId,
    assignment.node?.task?.entityId,
    assignment.node?.task?.updatedAt,
    assignment.node?.task?.title,
    assignment.node?.task?.blocks
  ]);

  // Mentor view has no URL slide-sync; land it on END here.
  // Keep AFTER the SET_ASSIGNMENT effect so this wins over its node-change START reset.
  useEffect(() => {
    if (!landOnEndSlide) return;
    dispatch({
      type: AssignmentTaskActionType.SHOW_SLIDE,
      payload: { slideType: AssignmentTaskSlideType.END_SLIDE, blockEntityId: null }
    });
  }, [landOnEndSlide, assignment.nodeEntityId]);

  return (
    <AssignmentTaskContext.Provider value={{ state, dispatch, isEditorMode, isReadOnly, source }}>
      {children}
    </AssignmentTaskContext.Provider>
  );
};

export const useAssignmentTask = () => {
  const context = useContext(AssignmentTaskContext);
  if (!context) {
    throw new Error('useAssignmentTask must be used within an AssignmentTaskProvider');
  }
  return context;
};
